import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Plus, Check, X, RefreshCw, RotateCcw, Trash2, FilePlus, ChevronDown, ChevronUp, Search, AlertTriangle, Pencil, ArrowDownWideNarrow,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUS_STYLES = {
  offen: { bg: "bg-amber-500/10", border: "border-amber-500/30", text: "text-amber-400", label: "Offen" },
  angenommen: { bg: "bg-emerald-500/10", border: "border-emerald-500/30", text: "text-emerald-400", label: "Angenommen" },
  abgelehnt: { bg: "bg-red-500/10", border: "border-red-500/30", text: "text-red-400", label: "Abgelehnt" },
};

const fmtDate = (d) => {
  if (!d) return "–";
  const p = d.split("-");
  return `${p[2]}.${p[1]}.${p[0]}`;
};

export default function OfferList({ orders = [], onDataChange }) {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [actionState, setActionState] = useState(null); // {offerId, action, ...}
  const [panelOpen, setPanelOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Form state
  const [formKunde, setFormKunde] = useState("");
  const [formProjekt, setFormProjekt] = useState("");
  const [formDate, setFormDate] = useState("");
  const [formNotes, setFormNotes] = useState("");

  // Action form state
  const [actDate, setActDate] = useState("");
  const [actWert, setActWert] = useState("");
  const [actOrderId, setActOrderId] = useState("new");
  const [actRecon, setActRecon] = useState("zugeordnet");
  const [actVersionDate, setActVersionDate] = useState("");
  const [editVersion, setEditVersion] = useState(null); // {offerId, nr}
  const [editVersionDate, setEditVersionDate] = useState("");

  const todayStr = new Date().toISOString().split("T")[0];

  const loadOffers = useCallback(async () => {
    try {
      const r = await api.get("/offers", { params: { active_only: false } });
      setOffers(r.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadOffers(); }, [loadOffers]);

  const handleCreate = async () => {
    if (editingId) {
      if (!formKunde.trim() || !formProjekt.trim()) {
        toast.error("Kunde und Projekt sind Pflicht");
        return;
      }
      try {
        await api.put(`/offers/${editingId}`, {
          kunde: formKunde, projekt: formProjekt, notes: formNotes,
        });
        toast.success("Angebot aktualisiert");
        setShowForm(false); setEditingId(null);
        setFormKunde(""); setFormProjekt(""); setFormDate(""); setFormNotes("");
        loadOffers();
        onDataChange?.();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Fehler");
      }
      return;
    }
    if (!formKunde.trim() || !formProjekt.trim() || !formDate) {
      toast.error("Kunde, Projekt und Versanddatum sind Pflicht");
      return;
    }
    try {
      await api.post("/offers", {
        kunde: formKunde, projekt: formProjekt,
        send_date: formDate, notes: formNotes,
      });
      toast.success("Angebot erstellt");
      setShowForm(false);
      setFormKunde(""); setFormProjekt(""); setFormDate(""); setFormNotes("");
      loadOffers();
      onDataChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const startEditOffer = (o) => {
    setEditingId(o.id);
    setFormKunde(o.kunde || "");
    setFormProjekt(o.projekt || "");
    setFormNotes(o.notes || "");
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false); setEditingId(null);
    setFormKunde(""); setFormProjekt(""); setFormDate(""); setFormNotes("");
  };

  const handleMove = async (offerId, direction) => {
    try {
      await api.patch(`/offers/${offerId}/move`, null, { params: { direction } });
      loadOffers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Verschieben");
    }
  };

  const handleSortByDate = async () => {
    try {
      await api.post("/offers/sort-by-date");
      toast.success("Nach Datum sortiert (neueste zuerst)");
      loadOffers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Sortieren");
    }
  };

  const handleAccept = async (offerId) => {
    if (!actDate) { toast.error("Entscheidungsdatum fehlt"); return; }
    try {
      await api.post(`/offers/${offerId}/accept`, {
        decision_date: actDate,
        auftragswert: parseFloat(actWert) || 0,
        existing_order_id: (actOrderId === "new" || actOrderId === "abgleich" || actOrderId === "nur_statistik") ? null : actOrderId,
        reconciliation: actOrderId === "abgleich" ? "abgleich_offen" : actOrderId === "nur_statistik" ? "nur_statistik" : "zugeordnet",
      });
      toast.success("Angebot angenommen");
      setActionState(null);
      loadOffers();
      onDataChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const handleReject = async (offerId) => {
    if (!actDate) { toast.error("Entscheidungsdatum fehlt"); return; }
    try {
      await api.post(`/offers/${offerId}/reject`, { decision_date: actDate });
      toast.success("Angebot abgelehnt");
      setActionState(null);
      loadOffers();
      onDataChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const handleReopen = async (offerId) => {
    try {
      await api.post(`/offers/${offerId}/reopen`);
      toast.success("Angebot wiedergeoeffnet");
      loadOffers();
      onDataChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const handleVersion = async (offerId) => {
    if (!actVersionDate) { toast.error("Versanddatum fehlt"); return; }
    try {
      await api.post(`/offers/${offerId}/version`, { send_date: actVersionDate });
      toast.success("Neue Version ergaenzt");
      setActionState(null);
      setActVersionDate("");
      loadOffers();
      onDataChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const handleSaveVersionDate = async (offerId, nr) => {
    if (!editVersionDate) { toast.error("Versanddatum fehlt"); return; }
    try {
      await api.patch(`/offers/${offerId}/version/${nr}`, { send_date: editVersionDate });
      toast.success("Versanddatum aktualisiert");
      setEditVersion(null); setEditVersionDate("");
      loadOffers();
      onDataChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const handleMarkStatsOnly = async (offerId) => {
    try {
      await api.post(`/offers/${offerId}/mark-stats-only`);
      toast.success("Auf reine Statistik-Zählung umgestellt");
      loadOffers();
      onDataChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const handleDeactivate = async (offerId) => {
    try {
      await api.post(`/offers/${offerId}/deactivate`);
      toast.success("Angebot deaktiviert");
      loadOffers();
      onDataChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const handleReactivate = async (offerId) => {
    try {
      await api.post(`/offers/${offerId}/reactivate`);
      toast.success("Angebot reaktiviert");
      loadOffers();
      onDataChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const handleDeletePermanent = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/offers/${deleteTarget.id}/permanent`);
      toast.success("Angebot endgültig gelöscht");
      setDeleteTarget(null);
      loadOffers();
      onDataChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const unlinkedOrders = orders.filter(
    (o) => o.status === "active" && !o.offer_id
  );

  const inputCls = "w-full h-8 bg-zinc-950 border border-zinc-800 text-xs text-zinc-300 font-mono px-2 focus:outline-none focus:border-zinc-600";

  if (loading) return <div className="h-20 bg-zinc-900/50 animate-pulse" />;

  const activeOffers = offers.filter((o) => o.active !== false);
  const inactiveOffers = offers.filter((o) => o.active === false);

  const PAGE_SIZE = 10;
  const q = search.trim().toLowerCase();
  const searching = q.length > 0;
  const filteredOffers = q
    ? activeOffers.filter(
        (o) =>
          (o.kunde || "").toLowerCase().includes(q) ||
          (o.projekt || "").toLowerCase().includes(q)
      )
    : activeOffers;
  const visibleOffers = showAll ? filteredOffers : filteredOffers.slice(0, PAGE_SIZE);

  return (
    <Card className="bg-card border-l-2 border-l-emerald-500/60 border-t-0 border-r border-b border-border" data-testid="offer-list">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setPanelOpen(!panelOpen)}
            className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-emerald-400 font-medium hover:text-emerald-300 transition-colors"
            data-testid="offer-panel-toggle"
          >
            {panelOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            Angebote verwalten ({activeOffers.length})
          </button>
          {panelOpen && (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-[10px] border-zinc-700 bg-zinc-950/50 text-zinc-200"
                onClick={handleSortByDate}
                data-testid="sort-offers-by-date-btn"
                title="Gesamte Liste nach Versanddatum sortieren, neueste zuerst"
              >
                <ArrowDownWideNarrow className="h-3 w-3 mr-1" />
                Nach Datum sortieren
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-[10px] border-zinc-700 bg-zinc-950/50 text-zinc-200"
                onClick={() => { if (showForm && !editingId) { cancelForm(); } else { cancelForm(); setShowForm(true); } }}
                data-testid="new-offer-btn"
              >
                <Plus className="h-3 w-3 mr-1" />
                Neues Angebot
              </Button>
            </div>
          )}
        </div>

        {panelOpen && (
        <div className="mt-4">
        {/* Create / Edit Form */}
        {showForm && (
          <div className="bg-zinc-950/80 border border-zinc-800/50 p-3 mb-4 space-y-2" data-testid="offer-form">
            <p className="text-[10px] uppercase tracking-wider text-zinc-400">
              {editingId ? "Angebot bearbeiten" : "Neues Angebot"}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input value={formKunde} onChange={(e) => setFormKunde(e.target.value)}
                placeholder="Kunde" className={inputCls} data-testid="offer-kunde" />
              <input value={formProjekt} onChange={(e) => setFormProjekt(e.target.value)}
                placeholder="Projektbezeichnung" className={inputCls} data-testid="offer-projekt" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {!editingId && (
                <input type="date" value={formDate} onChange={(e) => setFormDate(e.target.value)}
                  max={todayStr} className={inputCls} data-testid="offer-send-date" />
              )}
              <input value={formNotes} onChange={(e) => setFormNotes(e.target.value)}
                placeholder="Notizen (optional)" className={inputCls} />
            </div>
            {editingId && (
              <p className="text-[9px] text-zinc-500">
                Nur Kunde, Projekt und Notizen werden geändert. Status, Versionen, Entscheidungen
                und Auftragszuordnung bleiben erhalten.
              </p>
            )}
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="ghost" className="h-7 text-[10px]"
                onClick={cancelForm}>Abbrechen</Button>
              <Button size="sm" className="h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700"
                onClick={handleCreate} data-testid="offer-save-btn">
                {editingId ? "Speichern" : "Erstellen"}
              </Button>
            </div>
          </div>
        )}

        {/* Search */}
        {activeOffers.length > 0 && (
          <div className="relative mb-3">
            <Search className="h-3.5 w-3.5 text-zinc-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); }}
              placeholder="Suche nach Kunde oder Projekt…"
              className="w-full h-9 bg-zinc-950 border border-zinc-700 text-xs text-zinc-100 pl-8 pr-2 focus:outline-none focus:border-zinc-500 placeholder:text-zinc-500"
              data-testid="offer-search"
            />
          </div>
        )}
        {searching && (
          <p className="text-[9px] text-zinc-500 mb-2" data-testid="offer-search-hint">
            Zum Sortieren die Suche leeren – während einer Suche sind die Verschiebe-Schaltflächen deaktiviert.
          </p>
        )}

        {/* Offer List */}
        {activeOffers.length === 0 && !showForm ? (
          <p className="text-zinc-500 text-xs py-4 text-center">
            Noch keine Angebote erfasst.
          </p>
        ) : filteredOffers.length === 0 ? (
          <p className="text-zinc-500 text-xs py-4 text-center" data-testid="offer-no-results">
            Keine Angebote fuer &quot;{search}&quot; gefunden.
          </p>
        ) : (
          <div className="space-y-1.5" data-testid="offer-paged-list">
            {visibleOffers.map((o, idx) => {
              const st = STATUS_STYLES[o.status] || STATUS_STYLES.offen;
              const isExpanded = expanded === o.id;
              const isAction = actionState?.offerId === o.id;

              return (
                <div key={o.id} className="border border-zinc-800/40 bg-zinc-950/30" data-testid={`offer-${o.id.slice(0,8)}`}>
                  {/* Header */}
                  <div className="flex items-center gap-2 p-2">
                    {/* Move buttons */}
                    <div className="flex flex-col shrink-0">
                      <button
                        disabled={searching || idx === 0}
                        onClick={(e) => { e.stopPropagation(); handleMove(o.id, "up"); }}
                        className="text-zinc-500 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed"
                        data-testid={`offer-up-${o.id.slice(0,8)}`}
                        title="Nach oben"
                      >
                        <ChevronUp className="h-3 w-3" />
                      </button>
                      <button
                        disabled={searching || idx === visibleOffers.length - 1}
                        onClick={(e) => { e.stopPropagation(); handleMove(o.id, "down"); }}
                        className="text-zinc-500 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed"
                        data-testid={`offer-down-${o.id.slice(0,8)}`}
                        title="Nach unten"
                      >
                        <ChevronDown className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
                      onClick={() => setExpanded(isExpanded ? null : o.id)}>
                      <span className={`text-[8px] uppercase tracking-wider px-1.5 py-0.5 border ${st.bg} ${st.border} ${st.text}`}>
                        {st.label}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-zinc-200 truncate">{o.kunde} – {o.projekt}</p>
                      </div>
                      <span className="text-[9px] text-zinc-400 font-mono shrink-0" title="Letzter tatsächlicher Versand">
                        {fmtDate(o.last_send_date || o.first_send_date)}
                      </span>
                      {isExpanded ? <ChevronUp className="h-3 w-3 text-zinc-500" /> : <ChevronDown className="h-3 w-3 text-zinc-500" />}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="px-3 pb-3 space-y-2 border-t border-zinc-800/30">
                      <div className="flex justify-end pt-2">
                        <Button size="sm" variant="outline" className="h-6 text-[9px] border-zinc-700 text-zinc-200"
                          onClick={() => startEditOffer(o)} data-testid={`offer-edit-${o.id.slice(0,8)}`}>
                          <Pencil className="h-2.5 w-2.5 mr-1" />Kunde / Projekt bearbeiten
                        </Button>
                      </div>
                      {/* Versions */}
                      <div className="mt-2">
                        <p className="text-[8px] text-zinc-500 uppercase tracking-wider mb-1">Versionen</p>
                        {(o.versions || []).map((v, i) => {
                          const isEditV = editVersion?.offerId === o.id && editVersion?.nr === v.nr;
                          return (
                            <div key={i} className="flex items-center gap-2 text-[10px] py-0.5">
                              <span className="text-zinc-500">V{v.nr}</span>
                              {isEditV ? (
                                <>
                                  <input type="date" value={editVersionDate} max={todayStr}
                                    onChange={(e) => setEditVersionDate(e.target.value)}
                                    className="h-6 bg-zinc-950 border border-zinc-700 text-[10px] text-zinc-200 font-mono px-1 focus:outline-none focus:border-zinc-500"
                                    data-testid={`offer-version-dateinput-${o.id.slice(0,8)}-${v.nr}`} />
                                  <button onClick={() => handleSaveVersionDate(o.id, v.nr)}
                                    className="text-emerald-400 hover:text-emerald-300" title="Speichern"
                                    data-testid={`offer-version-save-${o.id.slice(0,8)}-${v.nr}`}>
                                    <Check className="h-3 w-3" />
                                  </button>
                                  <button onClick={() => { setEditVersion(null); setEditVersionDate(""); }}
                                    className="text-zinc-500 hover:text-zinc-300" title="Abbrechen">
                                    <X className="h-3 w-3" />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <span className="font-mono text-zinc-400">{fmtDate(v.send_date)}</span>
                                  {v.replaced && <span className="text-zinc-700">(ersetzt)</span>}
                                  <button onClick={() => { setEditVersion({ offerId: o.id, nr: v.nr }); setEditVersionDate(v.send_date); }}
                                    className="text-zinc-500 hover:text-zinc-300" title="Versanddatum korrigieren"
                                    data-testid={`offer-version-edit-${o.id.slice(0,8)}-${v.nr}`}>
                                    <Pencil className="h-2.5 w-2.5" />
                                  </button>
                                </>
                              )}
                            </div>
                          );
                        })}
                        <p className="text-[9px] text-zinc-500 mt-1">
                          Letzter Versand: <span className="font-mono text-zinc-400">{fmtDate(o.last_send_date || o.first_send_date)}</span>
                        </p>
                      </div>

                      {/* Decision info */}
                      {o.decision_date && (
                        <div className="text-[10px]">
                          <span className="text-zinc-500">Entscheidung: </span>
                          <span className="font-mono text-zinc-400">{fmtDate(o.decision_date)}</span>
                          {o.auftragswert > 0 && (
                            <span className="text-zinc-400 ml-2">| {new Intl.NumberFormat("de-DE").format(o.auftragswert)} EUR</span>
                          )}
                        </div>
                      )}

                      {o.reconciliation === "abgleich_offen" && (
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-[9px] text-amber-400">Abgleich offen</p>
                          <Button size="sm" variant="outline" className="h-6 text-[9px] border-emerald-800/40 text-emerald-400"
                            onClick={() => handleMarkStatsOnly(o.id)}
                            data-testid={`offer-statsonly-btn-${o.id.slice(0,8)}`}
                            title="Nur in der Angebotsstatistik mitzählen – kein Auftrag nötig">
                            Nur in Statistik zählen
                          </Button>
                        </div>
                      )}
                      {o.reconciliation === "nur_statistik" && (
                        <p className="text-[9px] text-emerald-400" data-testid={`offer-statsonly-badge-${o.id.slice(0,8)}`}>
                          Nur in der Angebotsstatistik gezählt – kein Auftrag, keine Punkte.
                        </p>
                      )}

                      {/* Previous decisions */}
                      {o.previous_decisions?.length > 0 && (
                        <div>
                          <p className="text-[8px] text-zinc-600 uppercase mb-1">Fruehere Entscheidungen</p>
                          {o.previous_decisions.map((pd, i) => (
                            <p key={i} className="text-[9px] text-zinc-600">
                              {fmtDate(pd.date)} – {pd.type === "annahme" ? "Annahme" : "Ablehnung"}
                            </p>
                          ))}
                        </div>
                      )}

                      {/* Notizen */}
                      {o.notes && o.notes.trim() && (
                        <div data-testid={`offer-notes-${o.id.slice(0,8)}`}>
                          <p className="text-[8px] text-zinc-500 uppercase tracking-wider mb-1">Notizen</p>
                          <p className="text-[10px] text-zinc-400 whitespace-pre-wrap break-words">{o.notes}</p>
                        </div>
                      )}

                      {/* Action Forms */}
                      {isAction && actionState.action === "accept" && (
                        <div className="bg-zinc-900/50 p-2 space-y-2 border border-emerald-900/30">
                          <p className="text-[9px] text-emerald-400 uppercase tracking-wider">Annahme</p>
                          <div className="grid grid-cols-2 gap-2">
                            <input type="date" value={actDate} onChange={(e) => setActDate(e.target.value)}
                              max={todayStr} className={inputCls} placeholder="Entscheidungsdatum" />
                            <input type="number" value={actWert} onChange={(e) => setActWert(e.target.value)}
                              className={inputCls} placeholder="Auftragswert EUR" />
                          </div>
                          <div>
                            <p className="text-[8px] text-zinc-500 mb-1">Auftragszuordnung</p>
                            <select value={actOrderId} onChange={(e) => setActOrderId(e.target.value)}
                              className={`${inputCls} appearance-none`}>
                              <option value="new">Neuen Auftrag erstellen</option>
                              {unlinkedOrders.map((ord) => (
                                <option key={ord.id} value={ord.id}>
                                  Bestehend: {fmtDate(ord.decision_date)} ({ord.auftragswert > 0 ? `${new Intl.NumberFormat("de-DE").format(ord.auftragswert)} EUR` : "–"})
                                </option>
                              ))}
                              <option value="abgleich">Abgleich offen (spaeter zuordnen)</option>
                              <option value="nur_statistik">Nur in der Angebotsstatistik mitzählen</option>
                            </select>
                            {actOrderId === "nur_statistik" && (
                              <p className="text-[8px] text-emerald-400 mt-1">
                                Zählt nur in den Angebotskennzahlen und Quoten. Es entsteht kein Auftrag,
                                kein Auftragswert, keine Punkte und keine Tagesbuchung.
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2 justify-end">
                            <Button size="sm" variant="ghost" className="h-6 text-[9px]"
                              onClick={() => setActionState(null)}>Abbrechen</Button>
                            <Button size="sm" className="h-6 text-[9px] bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => handleAccept(o.id)}>Annehmen</Button>
                          </div>
                        </div>
                      )}

                      {isAction && actionState.action === "reject" && (
                        <div className="bg-zinc-900/50 p-2 space-y-2 border border-red-900/30">
                          <p className="text-[9px] text-red-400 uppercase tracking-wider">Ablehnung</p>
                          <input type="date" value={actDate} onChange={(e) => setActDate(e.target.value)}
                            max={todayStr} className={inputCls} />
                          <div className="flex gap-2 justify-end">
                            <Button size="sm" variant="ghost" className="h-6 text-[9px]"
                              onClick={() => setActionState(null)}>Abbrechen</Button>
                            <Button size="sm" className="h-6 text-[9px] bg-red-600 hover:bg-red-700"
                              onClick={() => handleReject(o.id)}>Ablehnen</Button>
                          </div>
                        </div>
                      )}

                      {isAction && actionState.action === "version" && (
                        <div className="bg-zinc-900/50 p-2 space-y-2 border border-sky-900/30">
                          <p className="text-[9px] text-sky-400 uppercase tracking-wider">Ersatzversion</p>
                          <input type="date" value={actVersionDate} onChange={(e) => setActVersionDate(e.target.value)}
                            max={todayStr} className={inputCls} />
                          <div className="flex gap-2 justify-end">
                            <Button size="sm" variant="ghost" className="h-6 text-[9px]"
                              onClick={() => setActionState(null)}>Abbrechen</Button>
                            <Button size="sm" className="h-6 text-[9px] bg-sky-600 hover:bg-sky-700"
                              onClick={() => handleVersion(o.id)}>Senden</Button>
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      {!isAction && (
                        <div className="flex flex-wrap gap-1.5 pt-1">
                          {o.status === "offen" && (
                            <>
                              <Button size="sm" variant="outline" className="h-6 text-[9px] border-emerald-800/40 text-emerald-400"
                                onClick={() => { setActionState({ offerId: o.id, action: "accept" }); setActDate(""); setActWert(""); setActOrderId("new"); }}>
                                <Check className="h-2.5 w-2.5 mr-1" />Annehmen
                              </Button>
                              <Button size="sm" variant="outline" className="h-6 text-[9px] border-red-800/40 text-red-400"
                                onClick={() => { setActionState({ offerId: o.id, action: "reject" }); setActDate(""); }}>
                                <X className="h-2.5 w-2.5 mr-1" />Ablehnen
                              </Button>
                              <Button size="sm" variant="outline" className="h-6 text-[9px] border-sky-800/40 text-sky-400"
                                onClick={() => { setActionState({ offerId: o.id, action: "version" }); setActVersionDate(""); }}>
                                <FilePlus className="h-2.5 w-2.5 mr-1" />Version
                              </Button>
                            </>
                          )}
                          {(o.status === "angenommen" || o.status === "abgelehnt") && (
                            <Button size="sm" variant="outline" className="h-6 text-[9px] border-zinc-700 text-zinc-400"
                              onClick={() => handleReopen(o.id)}>
                              <RotateCcw className="h-2.5 w-2.5 mr-1" />Wiedereroeffnen
                            </Button>
                          )}
                          <Button size="sm" variant="outline" className="h-6 text-[9px] border-zinc-800 text-zinc-400"
                            onClick={() => handleDeactivate(o.id)}>
                            <Trash2 className="h-2.5 w-2.5 mr-1" />Deaktivieren
                          </Button>
                          <Button size="sm" variant="outline" className="h-6 text-[9px] border-red-900/50 text-red-400"
                            onClick={() => setDeleteTarget(o)} data-testid={`offer-delete-${o.id.slice(0,8)}`}>
                            <Trash2 className="h-2.5 w-2.5 mr-1" />Endgültig löschen
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Show more / less */}
        {filteredOffers.length > PAGE_SIZE && (
          <div className="mt-3 pt-2 border-t border-zinc-800/40 text-center" data-testid="offer-showmore">
            <Button size="sm" variant="ghost" className="h-7 text-[10px] text-emerald-400 hover:text-emerald-300"
              onClick={() => setShowAll(!showAll)}
              data-testid={showAll ? "offer-show-less" : "offer-show-all"}>
              {showAll
                ? "Weniger anzeigen"
                : `Alle Angebote anzeigen (${filteredOffers.length})`}
            </Button>
          </div>
        )}

        {/* Deactivated offers */}
        {inactiveOffers.length > 0 && (
          <details className="mt-3">
            <summary className="text-[10px] text-zinc-400 cursor-pointer hover:text-zinc-200">
              {inactiveOffers.length} deaktivierte Angebote
            </summary>
            <div className="space-y-1 mt-1.5">
              {inactiveOffers.map((o) => (
                <div key={o.id} className="flex items-center gap-2 p-2 border border-zinc-800/20 opacity-60">
                  <span className="text-[8px] text-zinc-400 uppercase px-1.5 py-0.5 border border-zinc-800/30">
                    Deaktiviert
                  </span>
                  <span className="text-[10px] text-zinc-400 flex-1 truncate">{o.kunde} – {o.projekt}</span>
                  <Button size="sm" variant="ghost" className="h-5 text-[8px] text-zinc-400"
                    onClick={() => handleReactivate(o.id)}>
                    <RefreshCw className="h-2.5 w-2.5 mr-0.5" />Reaktivieren
                  </Button>
                  <Button size="sm" variant="ghost" className="h-5 text-[8px] text-red-400"
                    onClick={() => setDeleteTarget(o)} data-testid={`offer-del-inactive-${o.id.slice(0,8)}`}>
                    <Trash2 className="h-2.5 w-2.5 mr-0.5" />Löschen
                  </Button>
                </div>
              ))}
            </div>
          </details>
        )}
        </div>
        )}
      </CardContent>

      {/* Bestätigung: Angebot endgültig löschen */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md" data-testid="offer-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-400" /> Angebot endgültig löschen
            </DialogTitle>
            <DialogDescription className="text-zinc-400 text-xs">
              Dieser Angebotsvorgang wird dauerhaft entfernt. Ein zugehöriger, berechtigter Auftrag
              bleibt erhalten (die Verknüpfung wird nur gelöst). Tagesdaten und Punkte bleiben unverändert.
            </DialogDescription>
          </DialogHeader>
          {deleteTarget && (
            <div className="bg-zinc-900/50 border border-zinc-800 p-3 space-y-1 text-xs" data-testid="offer-delete-details">
              <div className="flex justify-between"><span className="text-zinc-400">Kunde</span><span className="text-zinc-100">{deleteTarget.kunde || "–"}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Projekt</span><span className="text-zinc-100">{deleteTarget.projekt || "–"}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Versanddatum</span><span className="font-mono text-zinc-100">{fmtDate(deleteTarget.first_send_date)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Status</span><span className="text-zinc-100">{STATUS_STYLES[deleteTarget.status]?.label || deleteTarget.status}</span></div>
              {deleteTarget.auftragswert > 0 && (
                <div className="flex justify-between"><span className="text-zinc-400">Auftragswert</span><span className="font-mono text-zinc-100">{new Intl.NumberFormat("de-DE").format(deleteTarget.auftragswert)} EUR</span></div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" className="h-9 text-xs" onClick={() => setDeleteTarget(null)} data-testid="offer-delete-cancel">Abbrechen</Button>
            <Button className="h-9 text-xs bg-red-600 hover:bg-red-700" onClick={handleDeletePermanent} data-testid="offer-delete-confirm">Endgültig löschen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
